import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import type {ThemeColor} from '@nfq/colors';
import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * Optional. Specifies the color of the wrapper, which can be used for text or background color depending
     * on the CSS setup. The color is chosen from the `colors` palette defined in the `DefaultTheme`.
     * This allows for consistent color usage throughout the application.
     *
     * @default 'primaryFontColor'
     */
    color?: ThemeColor;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'List'
     */
    testId?: string;
}

/**
 * Renders a generic `List` wrapper component that can be styled dynamically based on provided props. This component is designed to encapsulate child elements,
 * potentially serving as a container for a list of items. The styling of the `List` can react to theme changes if `$reactsToTheme` is set to true, allowing the
 * component's color to adapt based on the current theme. This functionality is useful in applications where visual consistency with the overall theme is desired.
 *
 * The `List` component accepts a color prop (`$color`) to specify the color directly, enhancing flexibility in design. It also accepts any child components or elements,
 * making it highly versatile for various use cases, such as displaying a list of links, menu items, or any grouped content.
 *
 * @param props          The component props.
 * @param props.color    Optional. Specifies the color of the wrapper, which can be used for text or background color based on CSS setup.
 * @param props.children Child components or elements to be wrapped by this component.
 * @param props.testId   A unique identifier for the component, useful for testing purposes.
 * @returns A React component that serves as a flexible wrapper for various list-like content, with optional theme-reactive styling.
 *
 * @example
 * ```tsx
 * <List $color="primary" $reactsToTheme={true} testId="unique-list">
 *   <Item>Item 1</Item>
 *   <Item>Item 2</Item>
 * </List>
 * ```
 */
const List = observer(({children, color, testId = 'List'}: WithChildren<ComponentProps>) => (
    <Wrapper $color={color} data-cy={testId}>{children}</Wrapper>
));

List.displayName = 'List';

export {List};

interface WrapperProps {
    $color?: ThemeColor;
}

const Wrapper = styled.ul<WrapperProps>`
    color: ${({$color}) => ($color ?? null)};
    list-style: none;
    margin: 0;
    padding-left: 0;

    li {
        padding-left: 2.8rem;
        position: relative;

        &::before {
            background-color: currentColor;
            border-radius: 50%;
            content: '';
            display: block;
            height: 0.5rem;
            left: 1.1rem;
            margin: auto;
            position: absolute;
            top: 0.4em;
            width: 0.5rem;
        }

        &:not(:last-child) {
            margin-bottom: 1rem;
        }
    }
`;